"use client";

import { type FragmentOf, readFragment } from "gql.tada";
import { ADS_POLICIES } from "@/lib/authorization/policies/ads-analytics";
import { useCheckPolicy } from "@/lib/authorization/use-check-policy";
import { AdsSidebarSortable } from "./ads-sidebar-sortable";
import { createDragOverlayProject } from "./drag-overlay-utils";
import { AdsAnalyticsProjectItemFragment } from "./fragments";
import ProjectSidebarItem from "./project-sidebar-item";
import { SortableSidebarGroupsBase } from "./sortable-sidebar-groups-base";
import type { AAProject } from "./types";

interface SortableAdsProjectsProps {
  workspaceId: string;
  projectsData: readonly FragmentOf<typeof AdsAnalyticsProjectItemFragment>[];
  currentPathname: string;
  lang: string;
}

export default function AdsSortableProjects({
  workspaceId,
  projectsData,
  currentPathname,
  lang,
}: SortableAdsProjectsProps) {
  const checkPolicy = useCheckPolicy();
  const canEditProjects = checkPolicy(ADS_POLICIES["ads-project:edit"]).ok;

  const projects: AAProject[] = projectsData
    .map((fragmentData) => {
      const project = readFragment(
        AdsAnalyticsProjectItemFragment,
        fragmentData,
      );
      return project
        ? {
            id: project.id,
            name: project.name,
            color: project.color,
          }
        : null;
    })
    .filter((project): project is AAProject => project !== null);

  const renderAdsProject = (
    project: AAProject & { isProjectActive: boolean },
    isDragging: boolean,
  ) => {
    // Parent active は board/list のみ。settings / detail は子行だけを active にする（SA/UA と同じ）
    const pathPattern = `.*/${encodeURIComponent(workspaceId)}/ads/${encodeURIComponent(project.id)}/(board|list)`;
    const linkPath = `/${encodeURIComponent(lang)}/${encodeURIComponent(workspaceId)}/ads/${encodeURIComponent(project.id)}`;

    const fragmentData = projectsData.find((data) => {
      const p = readFragment(AdsAnalyticsProjectItemFragment, data);
      return p?.id === project.id;
    });
    const adsProject = fragmentData
      ? readFragment(AdsAnalyticsProjectItemFragment, fragmentData)
      : null;
    const needsDataSetup = Boolean(
      adsProject && !adsProject.adsAnalytics?.latestDataDate,
    );

    const sidebarContent = fragmentData ? (
      <AdsSidebarSortable
        project={fragmentData}
        workspaceId={workspaceId}
        lang={lang}
      />
    ) : null;

    return (
      <ProjectSidebarItem
        projectId={project.id}
        projectName={project.name}
        projectColor={project.color || "#000000"}
        projectType="ads"
        workspaceId={workspaceId}
        isProjectActive={project.isProjectActive || needsDataSetup}
        isDragging={isDragging}
        linkHref={linkPath}
        activePattern={pathPattern}
        lang={lang}
      >
        {sidebarContent}
      </ProjectSidebarItem>
    );
  };

  const renderAdsProjectForDragOverlay = (
    project: AAProject & { isProjectActive: boolean },
  ) => {
    const fragmentData = projectsData.find((data) => {
      const p = readFragment(AdsAnalyticsProjectItemFragment, data);
      return p?.id === project.id;
    });

    const sidebarContent = fragmentData ? (
      <AdsSidebarSortable
        project={fragmentData}
        workspaceId={workspaceId}
        lang={lang}
      />
    ) : null;

    return createDragOverlayProject({
      projectId: project.id,
      projectName: project.name,
      projectColor: project.color || "#000000",
      projectType: "ads",
      workspaceId,
      lang,
      children: sidebarContent,
    });
  };

  return (
    <SortableSidebarGroupsBase
      workspaceId={workspaceId}
      items={projects}
      projectType="ads"
      currentPathname={currentPathname}
      renderItem={renderAdsProject}
      renderDragOverlayItem={renderAdsProjectForDragOverlay}
      disableSorting={!canEditProjects}
    />
  );
}
